<script>
import Vue from 'vue'

export default Vue.extend({
    name: 'AddressAutocomplete',

    props: {
        // Name of input field.  Not really necessary, but helpful if we use the form object down the road
        name: {
            required: false,
            type: String,
        },

        // Passed in data as part of a form set
        field: {
            required: false,
            type: Object,
        },

        // Passed in data as part of a form set
        resourceInstance: {
            required: false,
            type: Object,
        },

        // Properties to display after a selection is made, e.g. name.
        displayProp: {
            required: false,
            type: String,
            default: 'name',
        },

        // A list of css classes
        classes: {
            required: false,
            type: String,
            default: 'form-control',
        },

        // Force a name and do not use the autocomplete/lookup
        preSelectedValue: {
            required: false,
            type: [String, Number],
            default: '',
        },

        // Placeholder text for input
        placeholder: {
            required: false,
            type: String,
            default: '',
        },

        // To mark this field as required
        required: {
            required: false,
            type: Boolean,
            default: false,
        },

        // To mark this field as readonly/disabled
        readonly: {
            required: false,
            type: Boolean,
            default: false,
        },

        // Used to identify the model.  Useful for changed fields
        dataModel: {
            type: String,
            required: false,
        },

        // This is for a traditional array of options to be passed in
        // The list of options.  Should be an array of objects or a Rest Resource
        options: {
            required: false,
            type: Array,
            default() {
                return []
            },
        },

        // Properties of the options to search against, e.g. name, code, description
        searchProps: {
            required: false,
            type: Array,
            default() {
                return ['code', 'name', 'id', 'username']
            },
        },

        // This is to use Rest Resource classes
        // Should be a Rest Resource
        resourceClass: {
            required: false,
            type: Function,
        },

        resolveManagers: {
            required: false,
            type: Array,
            default: function() {
                return []
            },
        },

        // Primary filter to be used with resourceClass, e.g. ?filter=
        primaryFilter: {
            required: false,
            type: String,
            default: undefined,
        },

        // Filter options that are not the primary filter.  Helps with limiting options, e.g. { type__in: 'POSTED', 'CLOSED' }
        filterOptions: {
            required: false,
            type: Object,
            default() {
                return {}
            },
        },

        // Number of characters to collect before querying API
        minKeywordLength: {
            required: false,
            type: Number,
            default: 1,
        },
        defaultShowResources: {
            required: false,
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            selected: false, // If the user has made a selection
            showList: false, // Show the options list
            isError: false, // If the input or selected field is incorrect, show an error
            keyword: '', // The actual input field
            highlightedPosition: 0, // Current list item to highlight
            query: '', // Search sent to Rest Resource API
            resources: [], // Resources retrieved from search
        }
    },

    watch: {
        keyword(newValue, oldValue) {
            if (newValue === this.preSelectedValue) {
                return false // Forced display of input, no lookup
            }
            if (newValue !== oldValue && newValue.length > this.minKeywordLength) {
                this.lookup()
            }
        },

        // Handle changes made after mounting, e.g. resources loaded asynchronously
        preSelectedValue(newValue) {
            if (newValue) {
                this.keyword = newValue
            }
        },
    },

    computed: {
        // Find items that match the list based on the input
        optionMatches() {
            if (!this.keyword) return []
            if (this.resources) return this.resources
            else return []
        },
    },

    methods: {
        // When something is typed into the input field, show the list and make sure to highlight the first match
        onInput(value) {
            if (!value) {
                return
            }
            this.selected = false
            this.isError = false
            this.showList = (!!this.keyword && process.env.VUE_APP_ADDRESS_AUTOCOMPLETE === 'true')
            
        },

        // When leaving the input field, select the highlighted value and return to the parent component
        async onSelect(item) {
            if (!item) {
                return
            } else if (item.target && item.target.value === '') {
                if (this.isError) this.isError = false
                // allow empty value. If this field is required, will get error in checkform function.
                this.$emit('select', '')
                return
            } else if (process.env.VUE_APP_ADDRESS_AUTOCOMPLETE === 'true' && !this.showList) {
                return
            }

            let value = item.target.value
            let displayedValue
            let selectedOption

            // Does our selection match an option value?  Error out unless we set mustMatch to false
            selectedOption = this.optionMatches[this.highlightedPosition]
            if (selectedOption === undefined) {
                displayedValue = value
                selectedOption = value
                this.isError = false
            } else if (selectedOption.preview.address !== undefined) {
                displayedValue = selectedOption.preview.address
                selectedOption = await this.autoCompleteAddressByPost(value, this.highlightedPosition)
                this.isError = false
            } else {
                displayedValue = value
                this.isError = true
                return false
            }
            if (selectedOption == '' || selectedOption == null) selectedOption = value
            this.showList = false
            this.highlightedPosition = this.highlight
            this.keyword = displayedValue
            this.$emit('select', selectedOption)
            return true
        },

        onKeyDown(event) {
            let keyCode = event.which || event.keyCode

            if (keyCode === 13 || keyCode === 9) {
                this.onSelect() // Enter/Return
            } else if (keyCode === 38) {
                this.moveUp() // Up
            } else if (keyCode === 40) {
                this.moveDown() // Down
            } else if (keyCode === 192) {
                // Escape
                this.showList = false
                this.params.api.stopEditing()
            } else {
                // No action for now
            }

            event.stopPropagation()
        },

        // Move the highlighed item index up
        moveUp() {
            // Only scroll down if not past the last item on the list
            if (this.showList && this.optionMatches.length && this.highlightedPosition > 0) {
                this.highlightedPosition--

                // Else scroll to the bottom
            } else if (this.showList && this.optionMatches.length && this.highlightedPosition <= 0) {
                this.highlightedPosition = this.optionMatches.length - 1
            } else {
                return false
            }

            const element = 'list-items'
            // this.$refs[element][this.highlightedPosition].scrollIntoView()
            this.$refs[element][this.highlightedPosition].focus()
            return true
        },

        // Move the highlighed item index down
        moveDown() {
            // Only scroll down if not past the last item on the list
            if (this.showList && this.optionMatches.length && this.highlightedPosition < this.optionMatches.length - 1) {
                this.highlightedPosition++

                // Else scroll to the top
            } else if (this.showList && this.optionMatches.length) {
                this.highlightedPosition = 0
            } else {
                return false
            }

            const element = 'list-items'
            // this.$refs[element][this.highlightedPosition].scrollIntoView()
            this.$refs[element][this.highlightedPosition].focus()
            return true
        },
        autoCompleteAddressByPost(address1, index) {
            return new Promise(function(resolve, reject) {
                if (!address1 || index == undefined || index == null || index < 0) {
                    return
                }
                var data = JSON.stringify({
                    partialStreet: address1,
                })

                var xhr = new XMLHttpRequest()
                xhr.withCredentials = true

                xhr.open('POST', `https://api.postgrid.com/v1/addver/completions?index=${index}`)
                xhr.setRequestHeader('x-api-key', process.env.VUE_APP_POSTGRID_API_KEY)
                xhr.setRequestHeader('Content-Type', 'application/json')
                xhr.timeout = 15000
                xhr.onload = function() {
                    var resp = JSON.parse(xhr.responseText)
                    if (xhr.status === 200) {
                        resolve(resp.data)
                    } else {
                        reject(resp)
                    }
                }
                // late response
                xhr.ontimeout = function(e) {
                    reject()
                }
                xhr.send(data)
            })
        },
        autoCompleteAddress(address1) {
            return new Promise(function(resolve, reject) {
                if (address1.length === 0) {
                    return
                }
                var xhr = new XMLHttpRequest()
                xhr.withCredentials = true

                xhr.open('GET', `https://api.postgrid.com/v1/addver/completions?partialStreet=${address1}&provInsteadOfPC=true`)
                xhr.setRequestHeader('x-api-key', process.env.VUE_APP_POSTGRID_API_KEY)
                xhr.setRequestHeader('Content-Type', 'application/json')
                xhr.timeout = 15000
                xhr.onload = function() {
                    var resp = JSON.parse(xhr.responseText)
                    if (xhr.status === 200) {
                        resolve(resp.data)
                    } else {
                        reject(resp)
                    }
                }
                // late response
                xhr.ontimeout = function(e) {
                    reject()
                }
                xhr.send()
            })
        },
        // Rest resource lookup
        lookup: _.debounce(async function(oldValue) {
            if (!this.showList) return
            let searchTerm = this.keyword
            if (process.env.VUE_APP_ADDRESS_AUTOCOMPLETE === 'true' && searchTerm) {
                this.resources = await this.autoCompleteAddress(searchTerm)
                return this.resources
            }
        }, 250),
    },
    async mounted() {
        if (this.preSelectedValue) {
            this.keyword = this.preSelectedValue
        }
    },
})
</script>

<template>
    <div class="autocomplete-input">
        <input
            :class="[{ 'form-error': isError }, classes]"
            :name="name + Math.floor(Math.random() * 1000000 + 1)"
            :placeholder="placeholder"
            :required="required"
            :readonly="readonly"
            :autocomplete="'force_off_' + Math.floor(Math.random() * 1000000 + 1)"
            v-model="keyword"
            :data-model="dataModel"
            @input="onInput"
            @keyup.esc="showList = false"
            @keydown.down="moveDown"
            @keydown.up="moveUp"
            @keydown.enter="onSelect"
            @keydown.tab="onSelect"
            @blur="
                onSelect($event)
                showList = false
            "
        />
        <ul v-show="showList" class="options-list" ref="list">
            <li
                v-for="(option, index) in optionMatches"
                :key="index"
                :class="{ highlighted: index === highlightedPosition }"
                ref="list-items"
                @mouseleave="highlightedPosition = -1"
                @mouseenter="highlightedPosition = index"
                @mousedown="onSelect"
            >
                <slot style="vertical-align: middle;" name="item" :address="option.preview.address" :city="option.preview.city" :prov="option.preview.prov"
                    >{{ option.preview.address }} {{ option.preview.city }} {{ option.preview.prov }}</slot
                >
            </li>
        </ul>
    </div>
</template>

<style scoped>
.form-inline .form-control {
    width: 100%;
}
.options-list {
    /* list-style-type: none; */
    border: 1px solid #dbdbdb;
    min-width: 250px;
    max-width: 500px;
    min-height: 20px;
    max-height: 500px;
    overflow: scroll;
    margin-top: 5px;
    padding-inline-start: 0px;
    line-height: 2em;
    vertical-align: middle;
    background: white;
    z-index: 1060;
}
.options-list li {
    width: 100%;
    flex-wrap: wrap;
    margin: 0px;
    padding-left: 10px;
    border-bottom: 1px solid #eee;
    color: rgb(24, 37, 79);
    cursor: pointer;
    vertical-align: middle;
}
.options-list li.highlighted {
    background: #feddbb;
}
.form-error,
.form-error2 {
    background: yellow !important;
    opacity: 0.7;
    font-weight: bold;
    color: #cc0000 !important;
}
</style>
